// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "../styles/EventDetail.css";
import { formatEventTime, formatDateRangeLabel, formatEventTypeLabel } from "../utils/eventUtils";
import {
  addConventionFavorite,
  getConventionById,
  getConventionFavorites,
  removeConventionFavorite,
  updateConventionFavoriteReminder,
} from "../api/conventions";
import { getUser } from "../auth/auth";
import { useToast } from "../components/ToastProvider";
import { getRuntimeEnv } from "../config/runtimeEnv";

const mapEmbed = (event, apiKey) => {
  if (!apiKey) return null;
  const q = `${event.address || ""}, ${event.city || ""}, ${event.state || ""} ${event.zipCode || ""}`;
  return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(q)}`;
};

export default function EventDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const { showToast } = useToast();

  const [event, setEvent] = useState(null);
  const [savedEvents, setSavedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const groupedEvent = location.state?.groupedEvent || null;
  const groupedSourceIds = useMemo(() => {
    const ids =
      Array.isArray(groupedEvent?.sourceIds) && groupedEvent.sourceIds.length > 0
        ? groupedEvent.sourceIds
        : [String(groupedEvent?.primaryId || id)];
    return ids.map((item) => String(item));
  }, [groupedEvent, id]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [eventData, favorites] = await Promise.all([
          getConventionById(String(id)),
          user?.id ? getConventionFavorites(user.id) : Promise.resolve([]),
        ]);
        setEvent(eventData);
        const ids = (favorites || []).map((item) => item.conventionId);
        setSavedEvents(ids);
        const favorite = (favorites || []).find((item) => groupedSourceIds.includes(String(item.conventionId)));
        setReminderEnabled(Boolean(favorite?.reminderEnabled));
      } catch (error) {
        console.error("Failed loading event", error);
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [groupedSourceIds, id, user?.id]);

  const activeSavedConventionId = groupedSourceIds.find((sourceId) => savedEvents.includes(sourceId)) || String(id);
  const isSaved = groupedSourceIds.some((sourceId) => savedEvents.includes(sourceId));

  const handleToggleSave = async () => {
    if (!user?.id) {
      showToast("Please login to save events", "error");
      return;
    }

    try {
      if (isSaved) {
        await Promise.all(
          groupedSourceIds
            .filter((sourceId) => savedEvents.includes(sourceId))
            .map((sourceId) => removeConventionFavorite(user.id, sourceId)),
        );
        setSavedEvents((prev) => prev.filter((item) => !groupedSourceIds.includes(String(item))));
        setReminderEnabled(false);
      } else {
        const primaryConventionId = String(groupedEvent?.primaryId || id);
        await addConventionFavorite(primaryConventionId);
        setSavedEvents((prev) => (prev.includes(primaryConventionId) ? prev : [...prev, primaryConventionId]));
      }
    } catch (error) {
      console.error("Failed updating saved status", error);
      showToast("Failed to update event save state", "error");
    }
  };

  const handleToggleReminder = async () => {
    if (!user?.id || !isSaved) {
      showToast("Save the event first to set reminders", "error");
      return;
    }

    try {
      const next = !reminderEnabled;
      await updateConventionFavoriteReminder(activeSavedConventionId, { reminderEnabled: next, reminderLeadDays: 3 });
      setReminderEnabled(next);
      showToast(next ? "Reminder enabled" : "Reminder disabled", "success");
    } catch (error) {
      console.error("Failed to update reminder", error);
      showToast("Failed to update reminder", "error");
    }
  };

  const mapLink = useMemo(() => {
    if (!event) return "";
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${event.address || ""}, ${event.city || ""}, ${event.state || ""} ${event.zipCode || ""}`,
    )}`;
  }, [event]);

  const embedSrc = useMemo(
    () => (event ? mapEmbed(event, getRuntimeEnv("REACT_APP_MAPS_API_KEY")) : null),
    [event],
  );

  if (loading) {
    return (
      <div className="event-detail-page">
        <div className="event-detail-card">
          <h2>Loading event…</h2>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-detail-page">
        <div className="event-detail-card">
          <h2>Event not found</h2>
          <Link to="/events" className="btn-secondary">
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  const website = event.website || event.url || "";
  const backTarget = location.state?.from || "/events";
  const displayStartDate = groupedEvent?.groupedStartDate || event.startDateTime || event.startDate;
  const displayEndDate = groupedEvent?.groupedEndDate || event.endDateTime || event.endDate || event.startDate;
  const isGrouped = Number(groupedEvent?.dayCount || 1) > 1;

  return (
    <div className="event-detail-page">
      <div className="event-detail-header">
        <button type="button" className="link-button" onClick={() => navigate(backTarget)}>
          Back
        </button>
      </div>

      <div className="event-detail-card">
        <div className="event-detail-title">
          <h1>{event.name}</h1>
          <span className={`event-type ${(event.eventType || "CONVENTION").toLowerCase()}`}>
            {formatEventTypeLabel(event.eventType || "CONVENTION")}
          </span>
        </div>

        <div className="event-detail-section">
          <h3>Date and time</h3>
          <div className="detail-row">
            <span>{formatDateRangeLabel(displayStartDate, displayEndDate)}</span>
            <span>
              {isGrouped
                ? `${groupedEvent.dayCount} day convention`
                : `${formatEventTime(event.startDateTime || event.startDate)} - ${formatEventTime(event.endDateTime || event.endDate || event.startDate)}`}
            </span>
          </div>
        </div>

        <div className="event-detail-section">
          <h3>Location</h3>
          <div className="detail-row">
            <span>{event.venueName}</span>
            <span>
              {event.address}, {event.city}, {event.state} {event.zipCode}
            </span>
          </div>
          {embedSrc ? (
            <iframe
              title="Event location map"
              src={embedSrc}
              width="100%"
              height="280"
              style={{ border: 0, borderRadius: 10, marginTop: 8 }}
              loading="lazy"
              allowFullScreen
            />
          ) : (
            <div className="event-detail-disabled">Map unavailable: API key missing</div>
          )}
          <a className="btn-secondary" href={mapLink} target="_blank" rel="noopener noreferrer">
            Open in Maps
          </a>
        </div>

        <div className="event-detail-section">
          <h3>About this event</h3>
          <p>{event.description}</p>
        </div>

        <div className="event-detail-section">
          <h3>Game types</h3>
          <div className="chip-group">
            {(event.games || []).map((game) => (
              <span key={game} className="chip small">
                {game}
              </span>
            ))}
          </div>
        </div>

        <div className="event-detail-cta">
          {website ? (
            <a className="btn-primary" href={website} target="_blank" rel="noopener noreferrer">
              View Event Website
            </a>
          ) : (
            <button type="button" className="btn-primary" disabled>
              Website unavailable
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={handleToggleSave}>
            {isSaved ? "Saved" : "Save Event"}
          </button>
          <button type="button" className="btn-secondary" onClick={handleToggleReminder}>
            {reminderEnabled ? "Reminder On" : "Set Reminder"}
          </button>
        </div>

        <div className="event-detail-disabled">
          Trade-ready users attending will show here once the community attendance layer goes live.
        </div>
      </div>
    </div>
  );
}
